import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth, UserRole, ROLE_CONFIG } from '@/contexts/AuthContext'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ShieldX, Lock } from 'lucide-react'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRoles?: UserRole[]
  minRole?: UserRole
  permission?: string
  fallbackPath?: string
}

/**
 * ProtectedRoute component for role-based access control
 * 
 * Usage examples:
 * <ProtectedRoute requiredRoles={['admin']}>...</ProtectedRoute>
 * <ProtectedRoute minRole="manager">...</ProtectedRoute>
 * <ProtectedRoute permission="inventory:edit">...</ProtectedRoute>
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles,
  minRole,
  permission,
  fallbackPath = '/dashboard'
}) => {
  const { isAuthenticated, loading, user, hasRole, hasMinRole, hasPermission } = useAuth()
  const location = useLocation()

  // Show loading state while checking authentication
  if (loading) {
    return (
      <LoadingSpinner 
        variant="fullscreen"
        size="lg"
        message="Verifying Access"
        submessage="Checking your permissions..."
      />
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }

  // Check role-based access
  let hasAccess = true
  let denialReason = ''

  if (requiredRoles && requiredRoles.length > 0) {
    hasAccess = requiredRoles.some(role => hasRole(role))
    if (!hasAccess) {
      denialReason = `Required role: ${requiredRoles.map(r => ROLE_CONFIG[r]?.label || r).join(' or ')}`
    }
  } else if (minRole) {
    hasAccess = hasMinRole(minRole)
    if (!hasAccess) {
      denialReason = `Minimum role required: ${ROLE_CONFIG[minRole]?.label || minRole}`
    }
  } else if (permission) {
    hasAccess = hasPermission(permission)
    if (!hasAccess) {
      denialReason = `Missing permission: ${permission}`
    }
  }

  // Show access denied page
  if (!hasAccess) {
    return <AccessDenied userRole={user?.role} reason={denialReason} fallbackPath={fallbackPath} />
  }

  return <>{children}</>
}

// Access Denied Component
interface AccessDeniedProps {
  userRole?: UserRole
  reason?: string
  fallbackPath: string
}

const AccessDenied: React.FC<AccessDeniedProps> = ({ userRole, reason, fallbackPath }) => {
  const roleConfig = userRole ? ROLE_CONFIG[userRole] : null

  return (
    <div className="min-h-screen bg-gradient-space flex items-center justify-center p-4">
      <div className="glass-card max-w-md w-full text-center">
        {/* Icon */}
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
          <ShieldX className="w-10 h-10 text-red-400" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
        <p className="text-muted-foreground mb-6">
          You don't have permission to access this page.
        </p>

        {/* Current Role Badge */}
        {roleConfig && (
          <div className="mb-6">
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Your Role</span>
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${roleConfig.bgColor} ${roleConfig.color} mt-2`}>
              <Lock className="w-4 h-4" />
              <span className="font-semibold">{roleConfig.label}</span>
            </div>
          </div>
        )}

        {/* Reason */}
        {reason && (
          <p className="text-sm text-red-400 mb-6 p-3 bg-red-500/10 rounded-lg">
            {reason}
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <a
            href={fallbackPath}
            className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-colors"
          >
            Go to Dashboard
          </a>
          <a
            href="/auth/login"
            onClick={() => {
              localStorage.removeItem('isAuthenticated')
              localStorage.removeItem('user')
              localStorage.removeItem('token')
            }}
            className="w-full py-3 bg-muted text-foreground rounded-xl font-semibold hover:bg-muted/80 transition-colors"
          >
            Switch Account
          </a>
        </div>
      </div>
    </div>
  )
}

export default ProtectedRoute
